/**
 * Extra Mesai Onay Servisi (KARAR #13 güncel — onaylı model).
 *
 * Yetkili personel (extraMesaiYetkisi:true) çıkış saatini + 30 dk aşınca,
 * autoCheckoutCron bir `extraMesaiTalepleri` kaydı oluşturur (durum:'bekliyor').
 * BOSS/yönetici onaylar veya reddeder.
 *
 *  - onaylandi  → maas-hesap o gün için extra'yı ÖDER (çıkış saatinden itibaren tümü)
 *  - reddedildi → extra ödenmez + kayıt tavan anına otomatik çıkışla kapatılır
 *
 * Yetkisiz personelde bu akış YOK — cron doğrudan otomatik çıkış yazar.
 */

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, query, where, Timestamp,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import { bildirimGonder } from './bildirim-service'
import type { FsTimestamp } from '@/types'

const KOLEKSIYON = 'extraMesaiTalepleri'
const KAYITLAR = 'kayitlar'

export type ExtraMesaiDurumu = 'bekliyor' | 'onaylandi' | 'reddedildi'

export interface ExtraMesaiTalebi {
  id: string
  personelId: string
  personelIsim: string
  tarih: string          // YYYY-MM-DD (giriş günü)
  subeId: string
  girisKayitId: string   // hangi açık giriş
  girisMs: number
  tavanMs: number        // extra'nın başladığı an (vardiya bitişi / giriş+10sa)
  vardiyaId?: string
  durum: ExtraMesaiDurumu
  onaylayanId?: string
  onayTarihi?: FsTimestamp
  /** BOSS/yöneticinin onayladığı ek süre (dk). Vardiya bitişinden sonra
   *  bu kadar dakika ek mesai ödenir; aşılırsa ödenmez + cron oto-çıkış yazar. */
  onayliEkDakika?: number
  sonBildirimMs?: number
  olusturmaTarihi: FsTimestamp
  guncellenmeTarihi: FsTimestamp
}

/** Karar bekleyen extra mesai talepleri (BOSS/yönetici feed). */
export async function bekleyenExtraTalepler(): Promise<ExtraMesaiTalebi[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, KOLEKSIYON), where('durum', '==', 'bekliyor'))
  const snap = await getDocs(q)
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as ExtraMesaiTalebi)
    .sort((a, b) => b.tavanMs - a.tavanMs)
}

/** Bir personelin onaylanmış extra talepleri (maas-hesap için, dönem filtreli). */
export async function personelOnayliExtralar(
  personelId: string,
  ayPrefix: string  // "YYYY-MM"
): Promise<ExtraMesaiTalebi[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, KOLEKSIYON), where('personelId', '==', personelId))
  const snap = await getDocs(q)
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as ExtraMesaiTalebi)
    .filter((t) => t.durum === 'onaylandi' && t.tarih.startsWith(ayPrefix))
}

export async function extraTalebiGetir(id: string): Promise<ExtraMesaiTalebi | null> {
  const db = getFirestoreDb()
  const s = await getDoc(doc(db, KOLEKSIYON, id))
  if (!s.exists()) return null
  return { id: s.id, ...s.data() } as ExtraMesaiTalebi
}

/**
 * Onayla → extra ödenir, ANCAK vardiya bitişinden sonra en fazla
 * `onayliEkDakika` dakika. Personel bu süreyi aşarsa fazlası ödenmez ve
 * cron, onaylanan süre dolunca otomatik çıkış yazar.
 */
export async function extraOnayla(
  id: string,
  onaylayanId: string,
  onayliEkDakika: number
): Promise<void> {
  const db = getFirestoreDb()
  const talep = await extraTalebiGetir(id)
  if (!talep) throw new Error('Talep bulunamadı.')
  if (talep.durum !== 'bekliyor') throw new Error('Bu talep zaten sonuçlanmış.')

  const ekDk = Math.max(0, Math.round(onayliEkDakika))

  await updateDoc(doc(db, KOLEKSIYON, id), {
    durum: 'onaylandi',
    onayliEkDakika: ekDk,
    onaylayanId,
    onayTarihi: nowServer(),
    guncellenmeTarihi: nowServer(),
  })

  try {
    const saatMetni = ekSaatMetni(ekDk)
    await bildirimGonder({
      kullaniciId: talep.personelId,
      tipi: 'extraMesaiOnay',
      aciliyet: 'normal',
      baslik: '✅ Extra mesai onaylandı',
      mesaj: `${tarihEtiket(talep.tarih)} günü için ${saatMetni} extra mesain onaylandı. Bu süre dolunca sistem otomatik çıkış yapar.`,
      link: '/personel',
    })
  } catch (e) {
    console.warn('[extra] onay bildirimi gönderilemedi:', e)
  }
}

function ekSaatMetni(dk: number): string {
  if (dk % 60 === 0) return `${dk / 60} saat`
  return `${(dk / 60).toFixed(1)} saat`
}

/**
 * Reddet → extra ödenmez + kayıt tavan anına otomatik çıkışla kapatılır.
 */
export async function extraReddet(id: string, onaylayanId: string): Promise<void> {
  const db = getFirestoreDb()
  const talep = await extraTalebiGetir(id)
  if (!talep) throw new Error('Talep bulunamadı.')
  if (talep.durum !== 'bekliyor') throw new Error('Bu talep zaten sonuçlanmış.')

  // 1) Talebi reddet
  await updateDoc(doc(db, KOLEKSIYON, id), {
    durum: 'reddedildi',
    onaylayanId,
    onayTarihi: nowServer(),
    guncellenmeTarihi: nowServer(),
  })

  // 2) Kaydı tavan anına otomatik çıkışla kapat (eğer hâlâ açıksa)
  try {
    const sonKayit = await sonKayitTip(talep.personelId)
    if (sonKayit === 'giris') {
      const cid = doc(collection(db, KAYITLAR)).id
      await setDoc(doc(db, KAYITLAR, cid), {
        personelId: talep.personelId,
        subeId: talep.subeId,
        tip: 'cikis',
        yontem: 'otomatik',
        zaman: Timestamp.fromMillis(talep.tavanMs),
        clientTime: Date.now(),
        notlar: 'Extra mesai reddedildi — tavan anına otomatik çıkış',
      })
    }
  } catch (e) {
    console.warn('[extra] red sonrası otomatik çıkış yazılamadı:', e)
  }

  try {
    await bildirimGonder({
      kullaniciId: talep.personelId,
      tipi: 'extraMesaiOnay',
      aciliyet: 'normal',
      baslik: 'ℹ️ Extra mesai onaylanmadı',
      mesaj: `${tarihEtiket(talep.tarih)} günü için extra mesai onaylanmadı. Çıkış saatinden sonrası ödenmeyecek.`,
      link: '/personel',
    })
  } catch (e) {
    console.warn('[extra] red bildirimi gönderilemedi:', e)
  }
}

/** Personelin son kayıt tipini getir (açık mı kontrolü). */
async function sonKayitTip(personelId: string): Promise<'giris' | 'cikis' | null> {
  const db = getFirestoreDb()
  const q = query(collection(db, KAYITLAR), where('personelId', '==', personelId))
  const snap = await getDocs(q)
  let son: { tip: string; ms: number } | null = null
  for (const d of snap.docs) {
    const data = d.data() as { tip: string; zaman: Timestamp }
    const ms = data.zaman?.toMillis?.() ?? 0
    if (!son || ms > son.ms) son = { tip: data.tip, ms }
  }
  return (son?.tip as 'giris' | 'cikis') ?? null
}

function tarihEtiket(tarih: string): string {
  return tarih.split('-').reverse().join('.')
}
